// components/portal/BrandStoreGallery.tsx
//
// The brand's shops, seen: the best Google photos of its own shops (scripts/brands/store-gallery.ts),
// as the head of the Overview beside its logo, and as the Gallery tab. ChatGPT Library tiles: the
// picture is the tile, rounded 16, no frame, no caption; a photo opens with its title.
import React, { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { ChevronLeft, ChevronRight, X } from '@/components/ui/lucide-memo';
import { BrandMark } from '@/components/ui/BrandMark';
import type { LeasingBrandDTO } from '#types/types';

type Photo = NonNullable<LeasingBrandDTO['storeGallery']>[number];

// A tile shimmers until its photo has arrived, then the photo fades in over it (owner, 2026-10-06:
// 'they just appear, we should have animated placeholders loading').
const Tile = ({ photo, alt }: { photo: Photo; alt: string }) => {
  const [loaded, setLoaded] = useState(false);
  return (
    <div className={`aspect-[4/3] overflow-hidden rounded-[16px] ${loaded ? 'bg-[#1B1B1B]' : 'skeleton'}`}>
      <img src={photo.url} alt={alt} loading="lazy" onLoad={() => setLoaded(true)} onError={() => setLoaded(true)}
        className={`h-full w-full object-cover transition-[opacity,transform] duration-300 group-hover:scale-[1.03] ${loaded ? 'opacity-100' : 'opacity-0'}`} />
    </div>
  );
};
const LoadingTiles = ({ count, className }: { count: number; className: string }) => (
  <div className={`skeleton-in ${className}`} aria-busy="true" aria-label="Loading" role="status">
    {Array.from({ length: count }, (_, index) => <div key={index} className="skeleton aspect-[4/3] rounded-[16px]" />)}
  </div>
);

/** The logo, then the three best shop photos; a photo opens the Gallery. */
export const BrandOverviewHero = ({ name, logoUrl, photos, onOpenGallery, lang }: {
  // undefined: the brand's detail is still on its way.
  name: string; logoUrl: string | null | undefined; photos: Photo[] | undefined; onOpenGallery: () => void; lang: string;
}) => !photos ? <LoadingTiles count={4} className="grid grid-cols-4 gap-2" /> : (
  // No photos yet: the logo alone, centred, on the page itself rather than in an empty grey tile.
  <section aria-label={lang === 'fr' ? 'Logo et magasins' : 'Logo and shops'} className={photos.length ? 'grid grid-cols-4 gap-2' : 'flex justify-center py-2'}>
    <div className="flex aspect-[4/3] items-center justify-center">
      <BrandMark logoUrl={logoUrl} name={name} size={72} radius={16} />
    </div>
    {photos.slice(0, 3).map((photo) => (
      <button key={photo.url} type="button" onClick={onOpenGallery} className="group block text-left"
        aria-label={lang === 'fr' ? `Voir la galerie : ${photo.city ?? name}` : `Open the gallery: ${photo.city ?? name}`}>
        <Tile photo={photo} alt={`${name}${photo.city ? `, ${photo.city}` : ''}`} />
      </button>
    ))}
  </section>
);

/** The photo opened from the Gallery: full size, with its title, arrows between photos, Esc closes. */
const PhotoViewer = ({ name, photos, index, onIndex, onClose, lang }: {
  name: string; photos: Photo[]; index: number; onIndex: (next: number) => void; onClose: () => void; lang: string;
}) => {
  const photo = photos[index]!;
  const step = useCallback((delta: number) => onIndex((index + delta + photos.length) % photos.length), [index, onIndex, photos.length]);
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
      else if (event.key === 'ArrowRight') step(1);
      else if (event.key === 'ArrowLeft') step(-1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, step]);
  const fr = lang === 'fr';
  const title = `${name}${photo.city ? ` · ${photo.city}` : ''}`;
  const round = 'flex h-8 w-8 items-center justify-center rounded-full text-[#B4B4B4] transition-colors hover:bg-white/[0.08] hover:text-white';
  // Portalled to the body: the brand panel sits under a transformed ancestor, where a fixed
  // overlay would be clipped to the panel instead of covering the screen.
  return createPortal(
    <div className="fixed inset-0 z-[200] flex items-center justify-center bg-[#2A2A2A]/70 p-6 backdrop-blur-sm" onClick={onClose}>
      <div role="dialog" aria-modal="true" aria-label={title}
        className="flex max-h-[92vh] w-full max-w-5xl flex-col overflow-hidden rounded-[24px] border border-white/[0.06] bg-[#212121]"
        onClick={(event) => event.stopPropagation()}>
        <div className="flex items-start justify-between gap-4 px-5 pb-3 pt-4">
          <div className="min-w-0">
            <div className="font-semibold text-[#ECECEC]" style={{ fontSize: 16 }}>{title}</div>
            <div className="text-[#8E8E8E]" style={{ fontSize: 13 }}>
              {[photo.address, photo.author ? (fr ? `Photo : ${photo.author}` : `Photo: ${photo.author}`) : null, `${index + 1} / ${photos.length}`].filter(Boolean).join(' · ')}
            </div>
          </div>
          <button type="button" onClick={onClose} aria-label={fr ? 'Fermer' : 'Close'} className={round}><X size={16} /></button>
        </div>
        <div className="relative flex min-h-0 flex-1 items-center justify-center bg-[#000000]">
          <img src={photo.url} alt={title} className="max-h-[78vh] w-full object-contain" />
          {photos.length > 1 ? (<>
            <button type="button" onClick={() => step(-1)} aria-label={fr ? 'Photo précédente' : 'Previous photo'} className={`${round} absolute left-3 top-1/2 -translate-y-1/2 bg-[#212121]`}><ChevronLeft size={16} /></button>
            <button type="button" onClick={() => step(1)} aria-label={fr ? 'Photo suivante' : 'Next photo'} className={`${round} absolute right-3 top-1/2 -translate-y-1/2 bg-[#212121]`}><ChevronRight size={16} /></button>
          </>) : null}
        </div>
      </div>
    </div>,
    document.body,
  );
};

/** Every photo kept for the brand: pictures only, a click opens one with its title. */
export const BrandGallery = ({ name, photos, lang }: { name: string; photos: Photo[] | undefined; lang: string }) => {
  const [open, setOpen] = useState<number | null>(null);
  if (!photos) return <LoadingTiles count={6} className="grid grid-cols-2 gap-2 py-2 sm:grid-cols-3" />;
  if (!photos.length) {
    return (
      <p className="py-6 text-[#8E8E8E]" style={{ fontSize: 14 }}>
        {lang === 'fr' ? "Pas encore de photo de ses magasins." : 'No photos of its shops yet.'}
      </p>
    );
  }
  return (
    <>
      <div className="grid grid-cols-2 gap-2 py-2 sm:grid-cols-3">
        {photos.map((photo, index) => (
          <button key={photo.url} type="button" onClick={() => setOpen(index)} className="group block"
            aria-label={lang === 'fr' ? `Ouvrir la photo${photo.city ? ` : ${photo.city}` : ''}` : `Open the photo${photo.city ? `: ${photo.city}` : ''}`}>
            <Tile photo={photo} alt={`${name}${photo.city ? `, ${photo.city}` : ''}`} />
          </button>
        ))}
      </div>
      {open !== null ? <PhotoViewer name={name} photos={photos} index={open} onIndex={setOpen} onClose={() => setOpen(null)} lang={lang} /> : null}
    </>
  );
};
